import { CompanyCompanyConversation } from 'apis/CompanyAPI/conversations/conversationTypes';
import { ProfileType, TinyCompanyDTO } from 'types/company';
import { UserProfile } from 'types/user';
import { communityUrls, companyUrls } from 'urls';
import { InboxConversation } from './useCompanyInbox';

/** The other side of a conversation, as the inbox routes address it. */
export type InboxRecipient = { type: 'company'; companyId: number } | { type: 'user'; cwUserId: UUID };

/**
 * Where a conversation with this recipient lives. The two sections of the app mount the same inbox
 * under different roots, so everything that links into it has to pick between them.
 */
export function inboxThreadUrl(context: ProfileType, entitySlug: string, recipient: InboxRecipient) {
  const inbox = context === 'company' ? companyUrls.inbox : communityUrls.inbox;

  return recipient.type === 'company'
    ? inbox.viewCompany(entitySlug, recipient.companyId)
    : inbox.view(entitySlug, recipient.cwUserId);
}

export function getOtherCompany(conversation: CompanyCompanyConversation, myCompanyId: number): TinyCompanyDTO {
  return conversation.companiesConversation.company.id === myCompanyId
    ? conversation.companiesConversation.otherCompany
    : conversation.companiesConversation.company;
}

/**
 * Every cwUserId we need a profile for to render the list: the person on the other side of a
 * user-company thread, plus whoever wrote the last message in any thread (used for the
 * "Lars: ..." prefix on the preview).
 */
export function getProfileIdsToFetch(conversations: InboxConversation[]): UUID[] {
  const ids = conversations.flatMap(conversation => [
    conversation.type === 'user-company' ? conversation.companyConversation.cwUserId : undefined,
    conversation.latestReply?.creatorCwUserId,
  ]);

  return Array.from(new Set(ids.filter((id): id is UUID => !!id)));
}

export function getPreviewText(conversation: InboxConversation): string {
  const latestReply = conversation.latestReply;

  if (!latestReply) return '';
  if (latestReply.content.length > 0) return latestReply.content;

  const attachmentCount = latestReply.attachments.length;
  if (attachmentCount > 0) {
    return `Sent ${attachmentCount} ${attachmentCount === 1 ? 'file' : 'files'}`;
  }

  return '';
}

/**
 * Who sent the last message, as it should read in front of the preview.
 *
 * Only returned when it tells the reader something the row doesn't already: "You", or the person
 * who wrote on behalf of the other company. On a 1:1 thread the sender is the counterparty, whose
 * name is already the row title, so prefixing it would just read "Ada Lovelace: Ada Lovelace: ...".
 * Undefined when we can't resolve a name, so the caller renders the preview without a dangling prefix.
 */
export function getSenderPrefix(
  conversation: InboxConversation,
  myCwUserId: string | undefined,
  userProfiles: UserProfile[],
): string | undefined {
  const creatorCwUserId = conversation.latestReply?.creatorCwUserId;

  if (!creatorCwUserId) return undefined;
  if (creatorCwUserId === myCwUserId) return 'You';
  if (conversation.type === 'user-company' && creatorCwUserId === conversation.companyConversation.cwUserId) {
    return undefined;
  }

  return userProfiles.find(profile => profile.cwUserId === creatorCwUserId)?.name;
}
